import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listBacklinks,
  createBacklink,
  updateBacklink,
  deleteBacklink,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Link2,
  ExternalLink,
  Trash2,
  Plus,
  CheckCircle2,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const STATUS_META = {
  planned:   { label: "Planned",   color: "bg-[#EAE7DD] text-[#1E392A]/70" },
  submitted: { label: "Submitted", color: "bg-[#E27D60]/15 text-[#a8492a]" },
  live:      { label: "Live",      color: "bg-[#85CDCB]/30 text-[#1E392A]" },
  rejected:  { label: "Rejected",  color: "bg-[#a8492a]/15 text-[#a8492a]/80" },
};

const PRIORITY_BADGE = {
  high:   "bg-[#E27D60]/15 text-[#a8492a]",
  medium: "bg-[#85CDCB]/25 text-[#1E392A]",
  low:    "bg-[#EAE7DD] text-[#1E392A]/65",
};

const EMPTY_FORM = { source: "", url: "", category: "Directory", status: "planned", notes: "" };

export default function BacklinkTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    try {
      const fresh = await listBacklinks();
      setData(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load backlinks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.source.trim()) {
      toast.error("Source is required");
      return;
    }
    setSavingNew(true);
    try {
      await createBacklink({
        ...form,
        source: form.source.trim(),
        url: form.url.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      toast.success("Backlink logged");
      setForm(EMPTY_FORM);
      setShowForm(false);
      await refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't create backlink");
    } finally {
      setSavingNew(false);
    }
  };

  const onUpdateStatus = async (id, status) => {
    try {
      await updateBacklink(id, { status });
      toast.success("Updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this backlink from the tracker?")) return;
    try {
      await deleteBacklink(id);
      toast.success("Removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  const prefillFromSuggestion = (sugg) => {
    setForm({
      source: sugg.name,
      url: sugg.url || "",
      category: sugg.category || "Directory",
      status: "planned",
      notes: "",
    });
    setShowForm(true);
    setTimeout(() => {
      document.getElementById("backlink-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (filter === "all") return data.items;
    return data.items.filter((it) => it.status === filter);
  }, [data, filter]);

  return (
    <section
      className="rounded-2xl bg-white border border-black/5 bento-shadow p-6 mb-8"
      data-testid="admin-backlink-tracker"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="tag-label text-[#1E392A]/55">Backlinks</div>
          <h2 className="font-display text-2xl mt-1 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#E27D60]" />
            Backlink tracker
          </h2>
          <p className="text-sm text-[#1E392A]/55 mt-1 max-w-2xl">
            Log every directory submission, blog mention, and guest post linking back to Plainly. Higher-quality backlinks = higher Google rankings.
          </p>
        </div>
        <Button
          onClick={() => { setShowForm((v) => !v); setForm(EMPTY_FORM); }}
          className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white"
          data-testid="backlink-add-toggle-btn"
        >
          {showForm ? <ChevronUp className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
          {showForm ? "Hide form" : "Add backlink"}
        </Button>
      </div>

      {/* Summary chips */}
      {data?.summary && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="backlink-summary">
          <SummaryChip label="Total" value={data.summary.total} active />
          <SummaryChip label="Live" value={data.summary.live} icon={<CheckCircle2 className="w-3 h-3" />} />
          <SummaryChip label="Submitted" value={data.summary.submitted} icon={<Clock className="w-3 h-3" />} />
          <SummaryChip label="Planned" value={data.summary.planned} />
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <form
          id="backlink-form"
          onSubmit={onCreate}
          className="rounded-xl bg-[#F7F5F0] border border-black/5 p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3"
          data-testid="backlink-add-form"
        >
          <div className="md:col-span-1">
            <label className="text-[11px] uppercase tracking-wider text-[#1E392A]/55">Source / site name</label>
            <Input
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              placeholder="e.g. Product Hunt"
              data-testid="backlink-source-input"
              className="mt-1 bg-white"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-[11px] uppercase tracking-wider text-[#1E392A]/55">Link URL (where Plainly is mentioned)</label>
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://…"
              data-testid="backlink-url-input"
              className="mt-1 bg-white"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#1E392A]/55">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1 bg-white" data-testid="backlink-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Launch">Launch</SelectItem>
                <SelectItem value="Reviews">Reviews</SelectItem>
                <SelectItem value="Directory">Directory</SelectItem>
                <SelectItem value="AI Directory">AI Directory</SelectItem>
                <SelectItem value="Community">Community</SelectItem>
                <SelectItem value="Social">Social</SelectItem>
                <SelectItem value="Guest Post">Guest Post</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#1E392A]/55">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1 bg-white" data-testid="backlink-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-[#1E392A]/55">Notes (optional)</label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Pending approval, scheduled for next week, etc."
              data-testid="backlink-notes-input"
              className="mt-1 bg-white"
            />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 mt-1">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={savingNew}
              className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white"
              data-testid="backlink-save-btn"
            >
              {savingNew ? "Saving…" : "Save backlink"}
            </Button>
          </div>
        </form>
      )}

      {/* Logged backlinks list */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="font-display text-lg">Logged ({data?.items?.length || 0})</h3>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-[#1E392A]/55">Filter</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid="backlink-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="planned">Planned</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-[#1E392A]/55 py-6 text-center">Loading backlinks…</div>
      )}
      {!loading && filteredItems.length === 0 && (
        <div className="text-sm text-[#1E392A]/55 py-6 text-center border border-dashed border-black/10 rounded-xl" data-testid="backlink-empty-state">
          {filter === "all"
            ? "No backlinks logged yet. Pick a suggestion below or add one manually."
            : `No backlinks with status "${filter}".`}
        </div>
      )}
      {!loading && filteredItems.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#1E392A]/55">
                <th className="pb-3 font-semibold">Source</th>
                <th className="pb-3 font-semibold">Category</th>
                <th className="pb-3 font-semibold">Status</th>
                <th className="pb-3 font-semibold">Link</th>
                <th className="pb-3 font-semibold text-right">Logged</th>
                <th className="pb-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((it) => (
                <tr key={it.id} className="border-t border-black/5" data-testid={`backlink-row-${it.id}`}>
                  <td className="py-3 pr-3 align-top">
                    <div className="font-medium text-[#1E392A]">{it.source}</div>
                    {it.notes && <div className="text-xs text-[#1E392A]/55 mt-0.5">{it.notes}</div>}
                  </td>
                  <td className="py-3 pr-3 align-top text-[#1E392A]/70">{it.category || "—"}</td>
                  <td className="py-3 pr-3 align-top">
                    <Select value={it.status} onValueChange={(v) => onUpdateStatus(it.id, v)}>
                      <SelectTrigger className={`h-7 px-2 text-[11px] rounded-full border-0 ${STATUS_META[it.status]?.color || "bg-[#EAE7DD]"} font-semibold uppercase tracking-wider`} data-testid={`backlink-status-${it.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="planned">Planned</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 pr-3 align-top">
                    {it.url ? (
                      <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E27D60] hover:underline inline-flex items-center gap-1 max-w-[220px] truncate">
                        <ExternalLink className="w-3 h-3 shrink-0" />
                        <span className="truncate">{it.url.replace(/^https?:\/\//, "")}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-[#1E392A]/40">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-3 align-top text-right text-[#1E392A]/65 whitespace-nowrap text-xs">
                    {it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 align-top text-right">
                    <button
                      onClick={() => onDelete(it.id)}
                      className="p-1.5 rounded-full hover:bg-[#a8492a]/10 text-[#a8492a]/70 hover:text-[#a8492a]"
                      title="Remove"
                      data-testid={`backlink-delete-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Suggestions */}
      {data?.suggestions && (
        <div className="mt-8 pt-6 border-t border-black/5">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-display text-lg flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#E27D60]" />
              Suggested submissions
            </h3>
            <div className="text-xs text-[#1E392A]/55">
              {data.suggestions.filter((s) => s.logged).length} of {data.suggestions.length} logged
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="backlink-suggestions">
            {data.suggestions.map((s) => (
              <div
                key={s.name}
                className={`rounded-xl border p-3 flex items-start gap-3 ${s.logged ? "border-[#85CDCB]/40 bg-[#85CDCB]/10" : "border-black/5 bg-white hover:border-[#E27D60]/30 transition-colors"}`}
                data-testid={`backlink-suggestion-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-[#1E392A]">{s.name}</span>
                    {s.logged && <CheckCircle2 className="w-3.5 h-3.5 text-[#1E392A]" />}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-[#1E392A]/55">{s.category}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${PRIORITY_BADGE[s.priority]}`}>
                      {s.priority}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#E27D60] hover:underline inline-flex items-center gap-1"
                    title="Open submission page"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open
                  </a>
                  {!s.logged && (
                    <button
                      onClick={() => prefillFromSuggestion(s)}
                      className="text-xs text-[#1E392A] hover:text-[#E27D60] underline-offset-2 hover:underline"
                      data-testid={`backlink-log-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      Log
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SummaryChip({ label, value, icon, active }) {
  return (
    <div
      className={`rounded-full px-3 py-1 text-xs flex items-center gap-1.5 ${active ? "bg-[#1E392A] text-[#F7F5F0]" : "bg-[#F7F5F0] text-[#1E392A]/70 border border-black/5"}`}
    >
      {icon}
      <span className="font-semibold">{value}</span>
      <span>{label}</span>
    </div>
  );
}
